import React, {Component} from 'react';
import {bind, pushWithParamsFromProps} from 'utils';
import {connect} from 'react-redux';
import queryString from 'query-string';
import {userSetInfo, userSetModel, userSetResult} from 'store/user/actions';
import {appSetComputing, appSetConfig} from 'store/app/actions';
import {dispatch} from 'store';
import Computing from 'components/general/computing/Computing';
import Footnotes from 'components/general/footnotes/Footnotes';
import PoweredBy from 'components/general/poweredBy/PoweredBy';
import TextBox from 'components/general/textBox/TextBox';
import SubmitButton from 'components/general/submitButton/SubmitButton';
import Back from 'components/general/svg/Back';
import APIRequest from 'api/APIRequest';
import {getLang, getLocalizedStrings} from 'locale/i18n';
import PropTypes from 'prop-types';
import Home from 'components/general/home/Home';
import appConfig from 'config';
import './Start.scss';
import {isValidBusinessId} from '../../../validators';
import Phone from '../phone/Phone';


const REQUIRED_PARAMS = ['source.id'];

class Start extends Component {
  constructor (...args) {
    super(...args);
    const params = queryString.parse(this.props.location.search);
    this.state = {
      code: params?.code ?? '',
      noData: false,
      yearData: [],
      companyData: [],
      loading: false,
      typingTimeout: 0,
      errors: {
        code: '',
        companySearch: ''
      },
      showHome: Start.getBooleanParamValue(params, 'showHome'),
      autoScroll: Start.getBooleanParamValue(params, 'autoScroll', false)
    };
    bind(this, [
      'onClick', 'handleChange', 'handleBack', 'validate', 'validateForm', 'getModel', 'processModel', 'getModelData', 'getModelDataPerYear', 'setNoData', 'getStartInput', 'searchCompany'
    ]);
  }

  searchCompany (value) {
    const self = this;
    const strings = getLocalizedStrings(this.props.app.config.lang);
    this.setState({
      loading: true,
      typingTimeout: setTimeout(async function () {
        this.api = new APIRequest();
        try {
          const modelResponse = await this.api.getCompanyData(value);
          self.setState({
            companyData: modelResponse.companies,
            loading: false,
            errors: {
              code: '',
              companySearch: ''
            }
          });
        } catch (error) {
          if (self.state.errors.code) {
            self.setState({
              errors: {
                ...self.state.errors,
                companySearch: strings['search-error']
              },
              companyData: []
            });
          }
          self.setState({
            loading: false
          });
        }
      }, 2000)
    });
  }

  componentDidMount() {
    const params = queryString.parse(this.props.location.search);
    const host = window.location.host.split(':')[0].toLowerCase();
    const lang = Start.getLangParamValue(params);
    const config = {
      lang: lang,
      source: Start.getSourceConfigFromParams(host, params),
      privacy: Phone.getConfigFromParams(params, lang),
      computing: Computing.getConfigFromParams(params),
      main: Start.getMainUiConfigFromParams(params)
    };
    this.props.dispatch(appSetConfig(config));

    const missingRequiredParams = REQUIRED_PARAMS.filter(param => {
      const hasParam = params?.[param]?.trim() !== '';
      const hasDefault = Start.getDefaultParamValue(host, param) !== undefined;
      return !hasParam && !hasDefault;
    });

    if (missingRequiredParams.length > 0) {
      const userInfo = {
        ...this.props.user?.info,
        error: true,
        details: `Missing required configuration parameters: ${missingRequiredParams}`
      };
      this.props.dispatch(userSetInfo(userInfo));
      this.props.dispatch(appSetComputing(false));
      pushWithParamsFromProps(this.props, '/info');
      return;
    }

    if (params?.code) {
      const code = params?.code ?? '';
      this.setState({showHome: false});
      const error = isValidBusinessId(code);
      if (!error) {
        this.setState({code, companyData: []});
        this.onClick();
      } else {
        this.searchCompany(code);
      }
    } else {
      this.setState({showHome: Start.getBooleanParamValue(params, 'showHome')});
    }
  }

  validate(name, value) {
    const strings = getLocalizedStrings(this.props.app.config.lang);
    return new Promise(resolve => {
      let errorVal = '';
      switch (name) {
        case 'code':
          errorVal = isValidBusinessId(value, strings['error-val-code-min'])
          break;
        default:
          break;
      }
      this.setState(state => {
        return {
          errors: {
            ...state.errors,
            [name]: errorVal,
            companySearch: ''
          },
          [name]: value
        };
      }, () => resolve());
    });
  }

  async validateForm () {
    let valid = true;
    await this.validate('code', this.state.code);
    Object.values(this.state.errors).forEach(
      (val) => val.length > 0 && (valid = false)
    );
    return valid;
  }

  async handleChange (e) {
    const target = e.target;
    const value = target.value;
    const name = target.name;
    this.validate(name, value);
    this.setState({ [name]: value, companyData: [] });

    if (this.state.typingTimeout) {
      clearTimeout(this.state.typingTimeout);
    }

    if (value) {
      this.searchCompany(value);
    } else {
      this.setState({ loading: false });
    }
  }

  handleBack () {
    this.setState({ noData: false });
  }

  onContactLink () {
    const strings = getLocalizedStrings(this.props.app.config.lang);
    const url = strings['contact-link'];
    window.open(url, '_blank');
  }

  async onClick () {
    const strings = getLocalizedStrings(this.props.app.config.lang);
    if (await this.validateForm()) {
      dispatch(appSetComputing(true));
      await new Promise(resolve => setTimeout(resolve, strings.timeout));
      this.getModel();
    }
  }

  async setNoData () {
    this.setState({
      code: '',
      noData: true
    });
    dispatch(appSetComputing(false));
  }

  async getModel() {
    try {
      const pCode = this.state.code.replace(/[- ]/g, '');
      this.api = new APIRequest();
      const result = await this.api.request('get', 'company-score', {businessId: pCode});

      if (!result || Object.keys(result).length <= 0) {
        await this.setNoData();
      }

      this.setState({yearData: result?.models ?? []});


      if (this.state.yearData[0]) {
        const userInfo = {
          ...this.props?.user?.info,
          code: this.state.code,
          year: this.state.yearData[0].year
        };

        dispatch(userSetInfo(userInfo));
        dispatch(userSetModel(result.company));
        dispatch(userSetResult(this.state.yearData));
        dispatch(appSetComputing(false));
        pushWithParamsFromProps(this.props, '/phone');
      } else {
        await this.setNoData();
      }

    } catch (error) {
      const userInfo = {
        ...this.props.user.info,
        error: true
      };
      dispatch(userSetInfo(userInfo));
      dispatch(appSetComputing(false));
      pushWithParamsFromProps(this.props, '/info');
    }
  }

  getStartInput () {
    const strings = getLocalizedStrings(this.props.app.config.lang);
    return (
      <div className='start-input'>
        <div>
          <TextBox label={strings['input-code']} name='code' type='text' value={this.state.code}
                   onHandleChange={this.handleChange} errors={this.state.errors} isFocus={this.state.autoScroll}/>
          {this.state.loading && <div className='search'>{strings.searching}</div>}
          {this.state.errors.companySearch && <div className='search-error'>{this.state.errors.companySearch}</div>}
          {this.state.companyData.length > 0 &&
            <div className='options'>
              {this.state.companyData.map((companyData) => {
                return <div className='select-option' key={companyData.businessId.value}
                            onClick={() => this.setState({
                              code: companyData.businessId.value,
                              companyData: []
                            })}>{`${companyData.names[0].name} (${companyData.businessId.value})`}</div>;
              })}
            </div>}
        </div>
        <SubmitButton label={strings['submit-button']} handleClick={this.onClick}/>
      </div>
    );
  }

  static get propTypes() {
    return {
      user: PropTypes.object,
      app: PropTypes.object,
      location: PropTypes.object
    };
  }

  render() {
    const strings = getLocalizedStrings(this.props.app.config.lang);
    return (
      <div>
        {this.props.app.computing === true && <Computing loadingStage={1}/>}
        {this.props.app.computing === false &&
          <>
            {this.state.showHome ? <Home>{this.getStartInput()}</Home> : (
              <div className='start-container app-container fade-animation'>
                {this.state.noData === true &&
                  <div className='error-container fade-animation'>
                    <div className='back-img' onClick={this.handleBack}>
                      <Back /> <span>{strings['back-link']}</span>
                    </div>
                    <div>
                      <h1 className='page-name'>{strings['error-header']}</h1>
                      <p className='p-error'>{strings['error-sub']}</p>
                      <ol>
                        <li>
                          {strings['error-1']}
                        </li>
                        <li>
                          {strings['error-2']}
                        </li>
                        <li>
                          {strings['error-3']}
                        </li>
                        <li>
                          {strings['error-4']}
                        </li>
                      </ol>
                      <p className='footnote'>{strings['error-footnote']}</p>
                      {this.getStartInput()}
                      <div className='error-foo-container'>
                        <p>{strings['error-foo-1']}</p>
                        <p>{strings['error-foo-2']}</p>
                        <SubmitButton label={strings['result-button']} handleClick={this.onContactLink} />
                      </div>
                      <Footnotes />
                    </div>
                    <div className='powered-by-container footer-powered-by'>
                      <PoweredBy />
                    </div>
                  </div>}
                {this.state.noData === false &&
                  <div className='main-start'>
                    <h1 className='app-name'>{strings['app-name']}</h1>
                    <p className='app-note'>{strings['app-note']}</p>
                    {this.getStartInput()}
                    <Footnotes />
                    <div className='powered-by-container footer-powered-by'>
                      <PoweredBy />
                    </div>
                  </div>}
              </div>
            )}
          </>}
      </div>
    );
  }

  static getSourceConfigFromParams(host, params) {
    return {
      id: params?.['source.id'] ?? this.getDefaultParamValue(host, 'source.id'),
      email: params?.['source.email'] ?? '',
      notificationEmail: params?.['source.notificationEmail'] ?? ''
    }
  }

  static getMainUiConfigFromParams(params = {}) {
    const {
      'main.customLogoUrl': logoUrl
    } = params;
    const showLangSelector = this.getBooleanParamValue(params, 'main.showLangSelector', true);
    return {
      logoUrl,
      showLangSelector
    };
  }

  static getDefaultParamValue(host, param) {
    return (appConfig.defaultParams[host] || {})[param];
  }

  static getBooleanParamValue(params, paramName, defValue = true) {
    const value = params?.[paramName];
    return value !== undefined ? String(value).toLowerCase() === "true" : defValue;
  }

  static getLangParamValue(params) {
    const value = params?.['lang'];
    return getLang(value);
  }

}


export default connect(state => ({ user: state.user, app: state.app }))(Start);
